import { Check, X, Minus } from "lucide-react";

/**
 * Comparison table — ResumeLab vs ChatGPT vs Indeed Resume Checker.
 * 9 features, brand-aligned (red accents for the winning column).
 */

const FEATURES = [
  { label: "Recruiter-grade scoring (0-100)", rl: "yes", gpt: "no", indeed: "partial" },
  { label: "ATS compatibility check (Workday, Greenhouse, Lever)", rl: "yes", gpt: "no", indeed: "yes" },
  { label: "Line-by-line rewrite suggestions", rl: "yes", gpt: "partial", indeed: "no" },
  { label: "Job-description targeted analysis", rl: "yes", gpt: "partial", indeed: "no" },
  { label: "AI cover-letter generation", rl: "yes", gpt: "yes", indeed: "no" },
  { label: "Multi-resume A/B comparison", rl: "yes", gpt: "no", indeed: "no" },
  { label: "Score-trend history + analytics", rl: "yes", gpt: "no", indeed: "partial" },
  { label: "PDF export (recruiter-formatted)", rl: "yes", gpt: "no", indeed: "no" },
  { label: "Built for African + global markets", rl: "yes", gpt: "no", indeed: "no" },
];

function Cell({ kind, highlight }) {
  if (kind === "yes") {
    return (
      <span
        className={`inline-flex items-center justify-center w-7 h-7 ${highlight ? "bg-[#EF4444] text-white" : "bg-zinc-900 text-white"}`}
        aria-label="Yes"
      >
        <Check className="w-4 h-4" strokeWidth={3} />
      </span>
    );
  }
  if (kind === "partial") {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 border border-zinc-300 text-zinc-500"
        aria-label="Partial"
        title="Partial"
      >
        <Minus className="w-4 h-4" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 text-zinc-300"
      aria-label="No"
    >
      <X className="w-4 h-4" strokeWidth={2.5} />
    </span>
  );
}

export default function CompareTable() {
  return (
    <section className="border-b border-zinc-200 bg-white" data-testid="compare-table">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">◆ THE FIELD</div>
            <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight leading-[1.05]">
              ResumeLab vs<br /><span className="brand-red">everything else.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-zinc-600 text-base lg:text-lg leading-relaxed">
            ChatGPT will rewrite your resume — but it won't tell you whether Workday's parser can read it. Indeed will scan it — but it won't rewrite your bullets. ResumeLab does both, plus the parts neither tool ships: A/B compare, score trends, recruiter-formatted PDF, and a model trained on what actually gets callbacks.
          </div>
        </div>

        <div className="border border-zinc-200 overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[640px]" data-testid="compare-table-grid">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th scope="col" className="px-5 lg:px-7 py-5 text-[11px] font-mono uppercase tracking-widest text-zinc-500 font-normal">
                  ◷ FEATURE
                </th>
                <th scope="col" className="px-3 lg:px-5 py-5 text-center bg-[#EF4444]/5 border-l-2 border-[#EF4444]">
                  <div className="font-display font-extrabold text-lg text-zinc-900">ResumeLab</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#EF4444] mt-1">RECOMMENDED</div>
                </th>
                <th scope="col" className="px-3 lg:px-5 py-5 text-center">
                  <div className="font-display font-bold text-base text-zinc-700">ChatGPT</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">GENERIC LLM</div>
                </th>
                <th scope="col" className="px-3 lg:px-5 py-5 text-center">
                  <div className="font-display font-bold text-base text-zinc-700">Indeed</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">JOB-BOARD TOOL</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f, i) => (
                <tr
                  key={f.label}
                  className={`border-b border-zinc-100 last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-zinc-50/40"}`}
                  data-testid={`compare-row-${i}`}
                >
                  <th scope="row" className="px-5 lg:px-7 py-4 text-sm text-zinc-800 font-normal align-middle">
                    {f.label}
                  </th>
                  <td className="px-3 lg:px-5 py-4 text-center bg-[#EF4444]/5 border-l-2 border-[#EF4444]/40">
                    <div className="inline-block"><Cell kind={f.rl} highlight={f.rl === "yes"} /></div>
                  </td>
                  <td className="px-3 lg:px-5 py-4 text-center">
                    <div className="inline-block"><Cell kind={f.gpt} /></div>
                  </td>
                  <td className="px-3 lg:px-5 py-4 text-center">
                    <div className="inline-block"><Cell kind={f.indeed} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-end gap-5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 bg-[#EF4444] inline-block" /> YES
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 border border-zinc-300 inline-block" /> PARTIAL
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 inline-block">
              <X className="w-3 h-3 text-zinc-300" strokeWidth={3} />
            </span>
            NOT AVAILABLE
          </span>
        </div>
      </div>
    </section>
  );
}
